/**
 * Relato de falha entregue no lugar da peça.
 *
 * Um proxy ou gateway no caminho do binário pode responder com HTTP 200 e, no
 * corpo, uma página de erro ("502 Bad Gateway" do nginx, a página de erro do
 * CloudFront, o XML de erro do S3) ou uma frase de erro em texto puro. Se isso
 * cair no Cache Storage do navegador, vira "a peça" nesta máquina: o cache é
 * consultado antes da rede, e nenhum conserto de servidor alcança a cópia.
 *
 * A barreira principal está no autenticador (`validarMagicBytes` em
 * `ProcessualCargaDatalakeService`, mesmos padrões de página de erro HTTP);
 * esta é a do navegador — recusa na escrita e cura o que já foi guardado.
 *
 * Os critérios são conservadores para não confundir peça de verdade:
 *  - só conteúdo pequeno é examinado (peça real tem quilobytes);
 *  - binário (PDF, imagem) não decodifica como UTF-8 estrito e passa direto;
 *  - HTML só é relato quando o `<title>`/`<h1>`/`<h2>` é o de uma página de
 *    erro HTTP — um despacho que MENCIONE "Unauthorized" no teor passa;
 *  - texto puro só é relato quando é curto e traz uma assinatura de erro HTTP.
 */

/** Acima disto é peça — nem se olha o conteúdo. Cobre a página do CloudFront. */
const MAX_INSPECT_BYTES = 4096;

const HTTP_ERROR_PAGE = new RegExp(
  '<(title|h1|h2)[^>]*>\\s*(?:\\d{3}\\s+)?(?:' +
    'bad gateway|gateway time-?out|service (?:temporarily )?unavailable|internal server error' +
    '|(?:error:\\s*)?the request could not be satisfied\\.?' +
    '|unauthorized|authorization required|forbidden|not found|too many requests' +
    ')\\s*</\\1>',
  'i',
);

/** XML de erro do S3/MinIO, ancorado no começo do corpo (o BOM o TextDecoder já tira). */
const OBJECT_STORAGE_ERROR = /^\s*(?:<\?xml[^>]*\?>\s*)?<Error>\s*<Code>/i;

/**
 * Texto puro só é relato quando é a frase de erro e nada mais ("502 Bad
 * Gateway", `{"message":"Internal server error"}`). Uma certidão que CITA o
 * erro ("o PJe retornou 503 Service Unavailable das 10h às 12h") é peça.
 */
const PLAIN_TEXT_ERROR =
  /^(?:\{\s*"(?:message|error)"\s*:\s*"|falha ao buscar o documento:\s*)?(?:\d{3}\s*[:-]?\s*)?(?:bad gateway|gateway time-?out|internal server error|service (?:temporarily )?unavailable|unauthorized)\.?(?::\s*\[no body\])?(?:"\s*\})?$/i;

/**
 * O texto do relato de falha que veio no lugar da peça, ou `null` quando o
 * conteúdo é documento de verdade.
 */
export async function detectFailureReport(blob: Blob): Promise<string | null> {
  if (blob.size === 0 || blob.size > MAX_INSPECT_BYTES) return null;

  let text: string;
  try {
    // `fatal`: PDF ou imagem falha aqui em vez de virar texto com caracteres
    // de substituição — binário não é relato.
    text = new TextDecoder('utf-8', { fatal: true }).decode(await blob.arrayBuffer()).trim();
  } catch {
    return null;
  }
  if (!text) return null;

  if (HTTP_ERROR_PAGE.test(text) || OBJECT_STORAGE_ERROR.test(text)) return text;

  // Qualquer outra marcação é documento, por menor que seja.
  if (text.includes('<')) return null;
  return PLAIN_TEXT_ERROR.test(text) ? text : null;
}
